import { useEffect } from 'react';
import type { UserRole } from '../lib/auth';

/**
 * MoreSheet — slide-up panel surfaced from the bottom "More" tab.
 *
 * Houses the moderator's account chip and the utility links that don't
 * belong on the primary tab bar: Moderator guide, Keyboard shortcuts,
 * Sign out, and (for lead/admin) admin tooling shortcuts.
 *
 * Renders as an overlay with a scrim. Closes on scrim tap, Escape, or
 * after each navigation. Body scroll is locked while open so the sheet
 * does not coexist with a scrolling background.
 */
interface MoreSheetProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (hash: string) => void;
  onOpenKeyboardHelp: () => void;
  onSignOut: () => void;
  onReportProblem?: () => void;
  userEmail?: string;
  userRole?: UserRole;
}

interface MoreLink {
  hash?: string;
  label: string;
  description?: string;
  action?: 'navigate' | 'keyboard' | 'signout' | 'report';
  testId: string;
  /** Render in the admin section. */
  adminOnly?: boolean;
  /** Render only when role is lead_moderator or admin. */
  leadOrAdminOnly?: boolean;
}

const LINKS: MoreLink[] = [
  {
    hash: '#/guide',
    label: 'Moderator guide',
    description: 'Decision standards, sensitivity, and house style',
    action: 'navigate',
    testId: 'more-guide',
  },
  {
    hash: '#/settings/security',
    label: 'Security',
    description: 'Manage passkeys for faster sign-in',
    action: 'navigate',
    testId: 'more-security',
  },
  {
    label: 'Keyboard shortcuts',
    description: 'For the desktop view',
    action: 'keyboard',
    testId: 'more-keyboard',
  },
  {
    hash: '#/',
    label: 'Moderator activity',
    description: 'Team review counts (last 7 days)',
    action: 'navigate',
    testId: 'more-activity',
    leadOrAdminOnly: true,
  },
  {
    hash: '#/issues',
    label: 'Issues',
    description: 'Problems people reported and errors the apps caught',
    action: 'navigate',
    testId: 'more-issues',
    leadOrAdminOnly: true,
  },
  {
    label: 'Report a problem',
    description: 'Something broken? Tell us and we will fix it',
    action: 'report',
    testId: 'more-report-problem',
  },
  {
    label: 'Sign out',
    action: 'signout',
    testId: 'more-signout',
  },
];

function roleLabel(role?: UserRole): string {
  if (role === 'admin') return 'Admin';
  if (role === 'lead_moderator') return 'Lead moderator';
  return 'Volunteer';
}

export default function MoreSheet({
  open,
  onClose,
  onNavigate,
  onOpenKeyboardHelp,
  onSignOut,
  onReportProblem,
  userEmail,
  userRole,
}: MoreSheetProps) {
  // Lock body scroll while the sheet is open. Restoring the previous
  // overflow value (rather than blindly setting it back to "") keeps any
  // outer scroll-lock the parent imposed.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Close on Escape so keyboard users have an explicit exit.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handle = (link: MoreLink) => {
    if (link.action === 'navigate' && link.hash) {
      onNavigate(link.hash);
      onClose();
      return;
    }
    if (link.action === 'keyboard') {
      onOpenKeyboardHelp();
      onClose();
      return;
    }
    if (link.action === 'report') {
      onClose();
      onReportProblem?.();
      return;
    }
    if (link.action === 'signout') {
      onSignOut();
      onClose();
      return;
    }
  };

  if (!open) return null;

  const role = userRole ?? 'volunteer';
  const isLeadOrAdmin = role === 'lead_moderator' || role === 'admin';

  return (
    <div
      className="more-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="More options"
      data-testid="more-sheet"
    >
      <button
        type="button"
        className="more-sheet__scrim"
        aria-label="Close more options"
        data-testid="more-sheet-scrim"
        onClick={onClose}
      />
      <div className="more-sheet__panel" role="document">
        <div className="more-sheet__handle" aria-hidden="true" />
        <div className="more-sheet__header">
          <div>
            <p className="more-sheet__email" data-testid="more-user-email">
              {userEmail ?? 'Signed in'}
            </p>
            <p className={`more-sheet__role role-chip role-chip--${role}`}>
              {roleLabel(userRole)}
            </p>
          </div>
          <button
            type="button"
            className="more-sheet__close"
            aria-label="Close"
            data-testid="more-sheet-close"
            onClick={onClose}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <ul className="more-sheet__list">
          {LINKS.filter((l) => !(l.leadOrAdminOnly && !isLeadOrAdmin))
            .filter((l) => !(l.action === 'report' && !onReportProblem))
            .filter((l) => !(l.adminOnly && role !== 'admin'))
            .map((link) => (
              <li key={link.testId}>
                <button
                  type="button"
                  className={`more-sheet__link${
                    link.action === 'signout' ? ' more-sheet__link--signout' : ''
                  }`}
                  data-testid={link.testId}
                  onClick={() => handle(link)}
                >
                  <span className="more-sheet__link-label">{link.label}</span>
                  {link.description && (
                    <span className="more-sheet__link-desc">{link.description}</span>
                  )}
                </button>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}
